<script lang="ts" setup>
import { message } from '../../utils/message';
import { ref, onMounted, onUnmounted, computed, watch, reactive } from "vue";
import { onBeforeRouteUpdate } from "vue-router";
import router from "../../router";
import { useAuthStore } from "../../store/authStore";
import api from "../../api";
import { DateTime } from "luxon";

const store = useAuthStore();
declare const google: any;

const loading  = ref(true);
const orderObject = reactive({ data: <any>null });
const myMapRef = ref<any>(null);
const mapCenter = { lat: -1.23266, lng: 36.77494 };
const routePath = ref<{ lat: number; lng: number }[]>([]);
const encodedPolyline = ref<string>('');
const driverMarker = ref<{ lat: number; lng: number } | null>(null);

// ── Delivery-document lightbox ───────────────────────────────────────────────
const docModal = reactive({ open: false, docs: <any>[], active: 0 });
const openDocModal  = (docs: any[], index: number) => { docModal.docs = docs; docModal.active = index; docModal.open = true; };
const closeDocModal = () => { docModal.open = false; };
// Mime-first image test — presigned Spaces URLs carry a ?X-Amz-... query string, so
// matching the file extension on the URL would wrongly classify images as files.
const isImage = (doc: any) =>
  /^image\//i.test(doc?.mime || '') || /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(doc?.src || '');
const docExt = (doc: any) => {
  const fromName = (doc?.title || '').match(/\.([a-z0-9]+)$/i);
  if (fromName) return fromName[1].toUpperCase();
  const m = (doc?.mime || '').split('/')[1];
  return m ? m.toUpperCase() : 'FILE';
};

// ── Rating + complaint widgets ───────────────────────────────────────────────
const COMPLAINT_CATEGORIES: { value: string; label: string }[] = [
  { value: 'late_delivery',    label: 'Late delivery' },
  { value: 'item_damaged',     label: 'Item damaged' },
  { value: 'item_missing',     label: 'Item missing or wrong' },
  { value: 'driver_conduct',   label: 'Driver conduct' },
  { value: 'vehicle_handling', label: 'Vehicle / handling' },
  { value: 'billing',          label: 'Billing / pricing' },
  { value: 'other',            label: 'Other' },
];

const rating = reactive({
  submitted: false,       // a rating exists on the server
  stars:     0,           // selected/submitted stars
  hover:     0,           // hover preview (interactive only)
  comment:   '',
  saving:    false,
});

const complaint = reactive({
  existing:   null as any,  // the server complaint row (if any)
  formOpen:   false,
  category:   '',
  body:       '',
  saving:     false,
});

// Which tab of the tracking panel is shown (keeps the panel from scrolling).
const trackTab = ref<'trip' | 'activity' | 'docs' | 'feedback'>('trip');

// Condense the header route: pickup + final drop-off, with middle stops collapsed
// to a "+N more stops" line so multi-stop orders don't make the header huge.
const headerStops = computed(() => {
  const p = orderObject.data?.paths || [];
  if (p.length <= 2) {
    return p.map((s: any, i: number) => ({ ...s, _type: i === 0 ? 'pickup' : 'drop', _last: i === p.length - 1 }));
  }
  return [
    { ...p[0], _type: 'pickup', _last: false },
    { _more: p.length - 2 },
    { ...p[p.length - 1], _type: 'drop', _last: true },
  ];
});

const canGiveFeedback = computed(() =>
  ['Delivered', 'Invoiced'].includes(orderObject.data?.status_text || '')
);
const complaintIsActive = computed(() =>
  ['Open', 'In Progress'].includes(complaint.existing?.status || '')
);

// Pull the rating/complaint widget UI back in line with the server snapshot.
const syncFeedbackState = () => {
  const r = orderObject.data?.rating;
  rating.submitted = !!r;
  rating.stars     = r?.stars || 0;
  rating.comment   = r?.comment || '';
  rating.hover     = 0;

  complaint.existing = orderObject.data?.complaint || null;
  complaint.formOpen = false;
  complaint.category = '';
  complaint.body     = '';
};

const submitRating = async () => {
  if (rating.saving || rating.stars < 1) {
    if (rating.stars < 1) message.warning('Please pick a star rating first');
    return;
  }
  const id = getOrderId();
  if (!id) return;
  rating.saving = true;
  try {
    await api.post(`customer/orders/${id}/rating`, {
      stars:   rating.stars,
      comment: rating.comment?.trim() || undefined,
    });
    rating.submitted = true;
    if (orderObject.data) {
      orderObject.data.rating = { stars: rating.stars, comment: rating.comment?.trim() || null };
    }
    message.success('Thanks for rating your delivery');
  } catch (err: any) {
    message.error(err.response?.data?.message || 'Could not save your rating — please try again.');
  } finally {
    rating.saving = false;
  }
};

const openComplaintForm  = () => { complaint.formOpen = true; };
const closeComplaintForm = () => { complaint.formOpen = false; complaint.category = ''; complaint.body = ''; };

// ── Customer Order Approval ──────────────────────────────────────────────
const approval = reactive({ rejecting: false, reason: '', saving: false });
// Documents shown while reviewing an order for approval.
const orderDocs    = computed<any[]>(() => (orderObject.data?.order_documents as any[]) || []);
const deliveryDocs = computed<any[]>(() => (orderObject.data?.documents as any[]) || []);
// Delivery-point documents grouped by destination (paths[0] = pickup; drop-offs = paths[1..]).
const deliveryDocGroups = computed<{ label: string; name: string; docs: any[] }[]>(() => {
  const paths = (orderObject.data?.paths as any[]) || [];
  const docs  = deliveryDocs.value;
  const drops = paths.slice(1);
  const groups = drops.map((s: any, i: number) => ({
    label: 'Delivery Point ' + (i + 1),
    name:  s.name || '',
    docs:  docs.filter((d: any) => Number(d.stop_id) === Number(s.id)),
  })).filter((g) => g.docs.length);
  // Safety net: docs whose stop didn't match a drop-off still get shown.
  const known = new Set(drops.map((s: any) => Number(s.id)));
  const orphans = docs.filter((d: any) => !known.has(Number(d.stop_id)));
  if (orphans.length) groups.push({ label: 'Delivery documents', name: '', docs: orphans });
  return groups;
});
// Unified render list: Order documents first, then one section per delivery point.
const docSections = computed<{ label: string; name: string; docs: any[] }[]>(() => {
  const sections: { label: string; name: string; docs: any[] }[] = [];
  if (orderDocs.value.length) sections.push({ label: 'Order documents', name: '', docs: orderDocs.value });
  for (const g of deliveryDocGroups.value) sections.push(g);
  return sections;
});

// Approval-thread rendering helpers
const APPR_LABELS: Record<string, string> = { Requested: 'Requested approval', Resubmitted: 'Resubmitted', Rejected: 'Rejected', Approved: 'Approved' };
const apprLabel = (a: string) => APPR_LABELS[a] || a;
const APPR_COLORS: Record<string, string> = { Requested: '#0061eb', Resubmitted: '#0061eb', Rejected: '#dc2626', Approved: '#16a34a' };
const apprColor = (a: string) => APPR_COLORS[a] || '#64748b';
const fmtApprTime = (ts: string) => {
  if (!ts) return '';
  const d = new Date(String(ts).replace(' ', 'T'));
  return isNaN(d.getTime()) ? ts : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const approveThisOrder = async () => {
  if (approval.saving) return;
  const id = getOrderId();
  if (!id) return;
  approval.saving = true;
  try {
    await api.post(`customer/orders/${id}/approve`);
    if (orderObject.data) {
      orderObject.data.approval_status = 'Approved';
      (orderObject.data.approval_log ||= []).push({ actor_type: 'customer', actor: 'You', action: 'Approved', note: null, created_at: new Date().toISOString() });
    }
    approval.rejecting = false;
    message.success('Order approved');
  } catch (err: any) {
    message.error(err.response?.data?.message || 'Could not approve the order — please try again.');
  } finally {
    approval.saving = false;
  }
};
const rejectThisOrder = async () => {
  if (approval.saving) return;
  const reason = approval.reason.trim();
  if (!reason) { message.warning('Please give a reason for rejecting.'); return; }
  const id = getOrderId();
  if (!id) return;
  approval.saving = true;
  try {
    await api.post(`customer/orders/${id}/reject`, { reason });
    if (orderObject.data) {
      orderObject.data.approval_status = 'Rejected';
      orderObject.data.approval_reason = reason;
      (orderObject.data.approval_log ||= []).push({ actor_type: 'customer', actor: 'You', action: 'Rejected', note: reason, created_at: new Date().toISOString() });
    }
    approval.rejecting = false;
    message.success('Order rejected');
  } catch (err: any) {
    message.error(err.response?.data?.message || 'Could not reject the order — please try again.');
  } finally {
    approval.saving = false;
  }
};

const submitComplaint = async () => {
  if (complaint.saving) return;
  if (!complaint.category) { message.warning('Please choose a complaint category'); return; }
  const id = getOrderId();
  if (!id) return;
  complaint.saving = true;
  try {
    const res = await api.post(`customer/orders/${id}/complaint`, {
      category: complaint.category,
      body:     complaint.body?.trim() || undefined,
    });
    const created = res.data?.data?.complaint;
    complaint.existing = created || {
      category:       complaint.category,
      category_label: COMPLAINT_CATEGORIES.find(c => c.value === complaint.category)?.label || complaint.category,
      status:         'Open',
    };
    if (orderObject.data) orderObject.data.complaint = complaint.existing;
    complaint.formOpen = false;
    complaint.body     = '';
    message.success('Complaint submitted — our team will review it shortly.');
  } catch (err: any) {
    if (err.response?.status === 409) {
      // A concurrent open complaint already exists — reflect it instead of erroring out.
      message.warning(err.response?.data?.message || 'You already have an open complaint for this order.');
      complaint.formOpen = false;
      if (err.response?.data?.data?.complaint) {
        complaint.existing = err.response.data.data.complaint;
        if (orderObject.data) orderObject.data.complaint = complaint.existing;
      }
    } else {
      message.error(err.response?.data?.message || 'Could not file your complaint — please try again.');
    }
  } finally {
    complaint.saving = false;
  }
};

// Customer-facing money formatter (no decimals, KES prefix) — shared by the breakdown.
const fmtMoney = (n: any) => 'KES ' + (Math.round(Number(n) || 0)).toLocaleString();

// SSE — module-level so it survives re-renders but doesn't leak onto window
let sseSource: EventSource | null = null;
let trackAbort: AbortController | null = null;
// Monotonic load counter — guards a slow async fetch (e.g. Directions) belonging to a
// previous order from resolving late and clobbering the route of the order now shown.
let loadSeq = 0;

const getOrderId = () => {
  const route = router.currentRoute.value;
  return route.params.id || route.query.orderId || route.query.order || null;
};

const loadOrder = async (orderId: any) => {
  loading.value = true;
  // New load: bump the counter (invalidates any in-flight Directions fetch from a
  // previous order) and clear the previous order's route + driver marker, so navigating
  // A->B never briefly shows A's path nor lets A's late response overwrite B's route.
  ++loadSeq;
  routePath.value = [];
  driverMarker.value = null;
  // Clear any half-typed approval action carried over from a previously-viewed order.
  approval.rejecting = false; approval.reason = ''; approval.saving = false;
  try {
    const res = await api.get(`customer/orders/${orderId}`);
    const order = res.data.data.order;

    // Transform to the shape the template expects
    orderObject.data = {
      order_details: {
        order_number:      order.order_ref,
        order_status:      mapStatusToCode(order.status),
        order_status_text: order.status,
        date_created:      order.created_at,
      },
      paths: (order.stops || []).map((s: any) => {
        const geo = s.location_json ? (typeof s.location_json === 'string' ? JSON.parse(s.location_json) : s.location_json) : null;
        return {
          id:     s.id,
          name:   s.location || '',
          lat:    geo?.lat  ? parseFloat(geo.lat)  : null,
          lng:    geo?.lng  ? parseFloat(geo.lng)  : null,
          status: s.status,
        };
      }),
      payment_details: {
        customer_amount: parseFloat(order.customer_amount) || 0,
        pricing_model:   order.pricing_model || null,
        distance_text:   order.distance_text || null,
        goods_weight:    order.goods_weight ?? null,
        breakdown:       order.pricing_snapshot?.breakdown ?? null,
      },
      driver_details: order.driver_name ? {
        name:         order.driver_name,
        phone_number: order.driver_phone,
        number_plate: order.plate_number,
        vehicle_type: order.vehicle_type || null,
        partner:      order.partner_name || null,
        rating:       order.driver_rating || null,
        photo:        order.driver_photo || null,
      } : null,
      driver: order.driver_name ? {
        name:  order.driver_name,
        phone: order.driver_phone,
      } : null,
      delivery_logs: order.delivery_logs || [],
      // Delivery-point documents (each carries stop_id to group under its stop).
      documents: (order.documents || []).map((d: any) => ({
        id:       d.id,
        stop_id:  d.stop_id,
        src:      d.url,
        title:    d.doc_type || d.original_name || 'Document',
        type:     d.doc_type || '',
        mime:     d.mime_type || '',
        // An approved image arrives with the stamp already drawn into it. A PDF
        // cannot carry one, so the viewer has to say it — this is what it reads.
        approved: !!d.approved,
      })),
      // Order-level documents (Order List, Manifest, …).
      order_documents: (order.order_documents || []).map((d: any) => ({
        id:    d.id,
        src:   d.url,
        title: d.doc_type || d.original_name || 'Document',
        type:  d.doc_type || '',
        mime:  d.mime_type || '',
      })),
      // Raw status string (the numeric code loses Delivered/Invoiced granularity) —
      // used to gate the rating + complaint widgets.
      status_text: order.status,
      rating:      order.rating || null,
      complaint:   order.complaint || null,
      approval_status: order.customer_approval_status || null,
      approval_reason: order.customer_approval_reason || null,
      approval_log:    Array.isArray(order.approval_log) ? order.approval_log : [],
    };

    // Hydrate the rating + complaint widget state from the server snapshot.
    syncFeedbackState();

    // Stash the stored encoded polyline (accurate road geometry) and render the
    // route. renderRoute() is decode-first and idempotent, so it can be safely
    // re-run once the Maps SDK's geometry library is ready, and a failed Directions
    // call can never clobber a good decoded path (the bug that blanked the line).
    encodedPolyline.value = order.polyline || '';
    renderRoute();

    // Order data is loaded — stop the spinner regardless of SSE
    loading.value = false;

    // Start the live stream for any non-terminal order — including ops-fulfilled ones
    // with no fleet driver — so the badge moves (e.g. Placed -> Delivered) live instead
    // of freezing at the load-time status. The server stream polls orders.status and
    // closes on a terminal state; onUnmounted/stopSSE releases it when the user leaves.
    if (!['Delivered', 'Invoiced', 'Cancelled'].includes(order.status)) {
      startSSE(orderId);
    }
  } catch (err: any) {
    const status = err.response?.status;
    message.error(status === 404
      ? "Order not found"
      : (err.response?.data?.message || "Couldn’t load this order — please try again."));
    loading.value = false;
  }
};

const mapStatusToCode = (status: string): number => {
  // Keys MUST match the API's order.status ENUM exactly (Placed/Intransit/Delivered/
  // Invoiced/Cancelled) — the old 'In Transit'/'Confirmed' keys never matched, so an
  // in-transit order fell through to 0 and lost its driver/step state.
  const map: Record<string, number> = {
    'Placed': 1, 'Intransit': 2, 'Delivered': 5, 'Invoiced': 5, 'Cancelled': 0,
  };
  return map[status] ?? 0;
};

const startSSE = (orderId: any) => {
  stopSSE();
  // Token lives in memory (window.__rileyToken); after a refresh it's gone, so also
  // send the HttpOnly riley_session cookie via credentials:'include' — the durable
  // credential. (The old localStorage('_riley') is always empty since the token refactor.)
  const token  = (window as any).__rileyToken || '';
  const base   = (import.meta.env.VITE_API_BASE || 'http://localhost:8081/api/v1').replace(/\/$/, '');
  const url    = `${base}/customer/orders/${orderId}/track`;

  // SSE with auth header isn't supported natively — use fetch with ReadableStream
  const ctrl = new AbortController();
  trackAbort = ctrl;

  fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    signal: ctrl.signal,
  }).then(async (resp) => {
    if (!resp.ok || !resp.body) return;
    loading.value = false;
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const payload = JSON.parse(line.slice(6));
            applyTrackUpdate(payload);
          } catch (e) {
            console.error('TrackOrder: SSE parse error', e);
          }
        }
        if (line === 'event: done') break;
      }
    }
  }).catch((e) => {
    if (e.name !== 'AbortError') {
      loading.value = false;
    }
  });
};

const stopSSE = () => {
  if (trackAbort) {
    trackAbort.abort();
    trackAbort = null;
  }
};

const applyTrackUpdate = (payload: any) => {
  loading.value = false;
  if (!orderObject.data) return;

  // Update status
  orderObject.data.order_details.order_status      = mapStatusToCode(payload.status);
  orderObject.data.order_details.order_status_text = payload.status;

  // Update stop statuses
  if (payload.stops && orderObject.data.paths) {
    payload.stops.forEach((stop: any, i: number) => {
      if (orderObject.data.paths[i]) {
        orderObject.data.paths[i].status = stop.status;
      }
    });
  }

  // Update driver location marker
  if (payload.driver_location) {
    driverMarker.value = {
      lat: payload.driver_location.lat,
      lng: payload.driver_location.lng,
    };
  }

  // Update driver info
  if (payload.driver_name) {
    orderObject.data.driver = {
      name:  payload.driver_name,
      phone: payload.driver_phone,
    };
  }
};

// Decode-first route rendering: prefer the order's stored encoded polyline (exact
// road geometry); fall back to the Directions API from the stop coordinates only
// when there's no stored polyline. Safe to call repeatedly — a successful decode
// always yields the same path, so it never clobbers a good route with a failed
// Directions call.
const renderRoute = () => {
  const g = (window as any).google;
  if (encodedPolyline.value && g?.maps?.geometry?.encoding) {
    try {
      routePath.value = g.maps.geometry.encoding
        .decodePath(encodedPolyline.value)
        .map((pt: any) => ({ lat: pt.lat(), lng: pt.lng() }));
      return;
    } catch (e) { /* fall through to the Directions fallback */ }
  }
  const validPaths = (orderObject.data?.paths || []).filter((p: any) => p.lat && p.lng);
  if (validPaths.length >= 2) fetchDirectionsRoute(validPaths);
};

const fetchDirectionsRoute = async (paths: any[]) => {
  if (paths.length < 2) return;
  const seq = loadSeq; // the load this fetch belongs to
  try {
    const directionsService = new google.maps.DirectionsService();
    const waypoints = paths.slice(1, -1).map((p: any) => ({
      location: { lat: p.lat, lng: p.lng }, stopover: true,
    }));
    const result = await directionsService.route({
      origin:      { lat: paths[0].lat, lng: paths[0].lng },
      destination: { lat: paths[paths.length - 1].lat, lng: paths[paths.length - 1].lng },
      waypoints,
      travelMode: google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    });
    if (seq !== loadSeq) return; // a newer order loaded while fetching — don't clobber it
    const path: { lat: number; lng: number }[] = [];
    result.routes[0].legs.forEach((leg: any) => {
      leg.steps.forEach((step: any) => {
        step.path.forEach((pt: any) => { path.push({ lat: pt.lat(), lng: pt.lng() }); });
      });
    });
    routePath.value = path;
  } catch {
    if (seq !== loadSeq) return; // stale fetch — leave the current order's route intact
    routePath.value = [];
  }
};

const updateBounds = () => {
  const mapInstance = myMapRef.value;
  try {
    if (mapInstance && orderDetailsLoaded.value) {
      const validPaths = (orderObject.data.paths || []).filter((p: any) => p.lat && p.lng);
      if (!validPaths.length && !driverMarker.value) return;
      mapInstance.$mapPromise.then((map: any) => {
        const bounds = new google.maps.LatLngBounds();
        validPaths.forEach((p: any) => bounds.extend({ lat: p.lat, lng: p.lng }));
        if (driverMarker.value) bounds.extend(driverMarker.value);
        if (!bounds.isEmpty()) map.fitBounds(bounds, { left: 460, top: 40, right: 40, bottom: 40 });
      });
    }
  } catch (e) { console.warn('fitBounds skipped:', e); }
};

watch(myMapRef, (googleMap) => {
  // Map ready => the Maps SDK (incl. the geometry library) is loaded; (re)render the
  // route — a decode that was skipped at load time because the SDK wasn't ready yet
  // now succeeds — then fit the viewport.
  if (googleMap) googleMap.$mapPromise.then(() => { renderRoute(); updateBounds(); });
});

watch(() => orderObject.data, (val) => {
  if (val?.paths?.length) updateBounds();
});

onMounted(() => {
  const orderId = getOrderId();
  if (orderId) loadOrder(orderId);
  else { loading.value = false; message.error("No order ID provided"); }

  // Stop stream immediately when the user logs out or switches account
  store.$onAction(({ name, after }) => {
    if (name === 'clearSession' || name === 'setSession') after(() => stopSSE());
  });
});

onUnmounted(() => { stopSSE(); });

onBeforeRouteUpdate((to) => {
  stopSSE();
  const id = to.params.id || to.query.orderId || to.query.order;
  if (id) loadOrder(id);
});

const orderDetailsLoaded = computed(() => orderObject.data !== null);

const mapPaths = computed(() =>
  (orderObject.data?.paths || []).filter(
    (p: any) => p.lat != null && p.lng != null && !isNaN(p.lat) && !isNaN(p.lng)
  )
);

const transformDate = (date: string) => {
  if (!date) return '';
  // Backend timestamps arrive as SQL datetime ("2026-06-27 11:37:46", space
  // separator), which fromISO can't parse — fall back to fromSQL before giving up
  // (otherwise luxon renders the literal string "Invalid DateTime").
  //
  // Read as UTC and printed unshifted, the same as the order list: the backend
  // stores Nairobi wall-clock, so letting luxon reinterpret it in the viewer's
  // own zone would move every timestamp for anyone travelling, and the two
  // screens would then disagree about when the same order was delivered.
  let dt = DateTime.fromISO(date, { zone: 'UTC' });
  if (!dt.isValid) dt = DateTime.fromSQL(date, { zone: 'UTC' });
  return dt.isValid ? dt.toFormat('dd MMM yyyy, HH:mm') : '';
};

const extractTextUntilBy = (text: string) => text?.split(' by ')[0] || text || '';

/**
 * When the order was completed. The API emits a synthetic 'Dropped' entry only
 * when the TMS never logged a real 'Delivered' event, so looking for 'Dropped'
 * alone left this blank on every order that had one. Take the last of either.
 */
const deliveredAt = computed<string>(() => {
  const logs = orderObject.data?.delivery_logs ?? [];
  for (let i = logs.length - 1; i >= 0; i--) {
    const t = logs[i]?.log_type;
    if (t === 'Dropped' || t === 'Delivered') return logs[i].log_time ?? '';
  }
  return '';
});

// Humanize the raw status ENUM for display ('Intransit' -> 'In transit') so the
// badge never shows the squashed enum spelling.
const STATUS_LABELS: Record<string, string> = { Intransit: 'In transit' };
const humanizeStatus = (status: string) => STATUS_LABELS[status] || status || '';

/**
 * The status pill in the header.
 *
 * These were light-background colours — a dark indigo #4f46e5 on a 12 % indigo
 * tint — sitting on a navy gradient. Dark on dark: "Delivered" was legible only
 * if you knew it was there. On this header the text has to be near-white and the
 * STATE is carried by the dot, which is the one thing that can afford to be a
 * saturated colour against navy.
 */
const statusColor = computed(() => {
  const s = orderObject.data?.order_details?.order_status;
  const base = { bg: 'rgba(255,255,255,0.16)', text: '#ffffff' };
  if (s === 1) return { ...base, dot: '#fbbf24' };                 // Placed
  if (s >= 2 && s < 5) return { ...base, dot: '#4ade80' };         // In transit
  if (s >= 5) return { ...base, dot: '#a5b4fc' };                  // Delivered / Invoiced
  return { ...base, dot: '#cbd5e1' };                              // Cancelled / unknown
});

const MARKER_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const getMarkerIcon = (index: number) => {
  const color = MARKER_COLORS[index % MARKER_COLORS.length];
  const label = String.fromCharCode(65 + index);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <filter id="shd${index}" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.25)"/>
      </filter>
      <ellipse cx="18" cy="42" rx="6" ry="2.5" fill="rgba(0,0,0,0.12)"/>
      <path d="M18 2C10.268 2 4 8.268 4 16c0 10 14 26 14 26s14-16 14-26C32 8.268 25.732 2 18 2z" fill="${color}" filter="url(#shd${index})"/>
      <circle cx="18" cy="16" r="8" fill="white" opacity="0.92"/>
      <text x="18" y="20" text-anchor="middle" font-size="9" font-weight="700" font-family="DM Sans, sans-serif" fill="${color}">${label}</text>
    </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 36, height: 44 },
    anchor: { x: 18, y: 44 },
  };
};

const driverMarkerIcon = {
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="18" fill="#0061eb" stroke="white" stroke-width="3"/>
      <text x="20" y="25" text-anchor="middle" font-size="16" fill="white">🚚</text>
    </svg>
  `)}`,
  scaledSize: { width: 40, height: 40 },
  anchor: { x: 20, y: 20 },
};

// Click a map pin → show the location name + type; click the driver vehicle →
// show the driver's name, plate, vehicle and phone.
const activeInfo = ref<{ position: { lat: number; lng: number }; eyebrow: string; title: string; lines: string[] } | null>(null);

const openStopInfo = (index: number) => {
  const m: any = mapPaths.value[index];
  if (!m) return;
  const total = mapPaths.value.length;
  activeInfo.value = {
    position: { lat: m.lat, lng: m.lng },
    eyebrow: index === 0 ? 'Pickup' : index === total - 1 ? 'Destination' : 'Drop-off',
    title: m.name || '—',
    lines: m.status ? [m.status] : [],
  };
};

const openDriverInfo = () => {
  if (!driverMarker.value) return;
  const d: any = orderObject.data?.driver_details || orderObject.data?.driver || {};
  activeInfo.value = {
    position: { lat: driverMarker.value.lat, lng: driverMarker.value.lng },
    eyebrow: 'Driver',
    title: d.name || 'Your driver',
    lines: [
      d.number_plate ? `Plate · ${d.number_plate}` : '',
      d.vehicle_type ? `Vehicle · ${d.vehicle_type}` : '',
      (d.phone_number || d.phone) ? `Phone · ${d.phone_number || d.phone}` : '',
      d.partner ? `Partner · ${d.partner}` : '',
    ].filter(Boolean),
  };
};

const polylineOptions = {
  strokeColor: "#2563eb", strokeOpacity: 1.0, strokeWeight: 5, geodesic: true,
};

const mapOptions = {
  styles: [
    { elementType: "geometry", stylers: [{ color: "#eaf0fb" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#3d4a5c" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
    { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#c8e6c9" }] },
    { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#388e3c" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#dce6f5" }] },
    { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#b8cef5" }] },
    { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#9ab8e8" }] },
    { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#0061eb" }] },
    { featureType: "road.local", stylers: [{ visibility: "simplified" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#a8cce0" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d6b8a" }] },
  ],
  disableDefaultUI: true,
  zoomControl: false,
};
</script>

<template>
  <div style="padding:0; position:relative; height:100%; width:100%; flex:1;" v-loading="loading">

    <!-- Customer approval — floating card, top-right over the map.
         The status reads from the pill and its dot. There is deliberately no
         coloured band across the card: a full-width wash of amber or green
         shouts louder than the decision it describes, and over a map that is
         already busy it reads as an alert rather than a state. -->
    <div v-if="orderDetailsLoaded && orderObject.data.approval_status" class="tap-card">
      <div class="tap-head">
        <span class="tap-eyebrow">Order approval</span>
        <span class="tap-status" :class="'tap-status--' + orderObject.data.approval_status.toLowerCase()">
          <span class="tap-status-dot"></span>
          {{ orderObject.data.approval_status === 'Pending' ? 'Pending approval' : orderObject.data.approval_status }}
        </span>
      </div>

      <!-- Conversation thread — the operations team on the left, you on the right -->
      <div v-if="orderObject.data.approval_log && orderObject.data.approval_log.length" class="tap-thread">
        <div v-for="(m, i) in orderObject.data.approval_log" :key="i"
             class="tap-row" :class="{ 'tap-row--you': m.actor_type === 'customer' }">
          <div class="tap-msg" :class="{ 'tap-msg--you': m.actor_type === 'customer' }">
            <div class="tap-msg-head">
              <span class="tap-msg-dot" :style="{ background: apprColor(m.action) }"></span>
              <span class="tap-msg-who">{{ m.actor_type === 'customer' ? 'You' : m.actor }}</span>
              <span class="tap-msg-tag" :style="{ color: apprColor(m.action), background: apprColor(m.action) + '14' }">{{ apprLabel(m.action) }}</span>
            </div>
            <p v-if="m.note" class="tap-msg-note">{{ m.note }}</p>
            <p class="tap-msg-time">{{ fmtApprTime(m.created_at) }}</p>
          </div>
        </div>
      </div>

      <!-- Legacy fallback (order predates the thread) -->
      <p v-else-if="orderObject.data.approval_status === 'Rejected' && orderObject.data.approval_reason" class="tap-legacy">
        <strong>Your reason:</strong> {{ orderObject.data.approval_reason }}
      </p>

      <template v-if="orderObject.data.approval_status === 'Pending'">
        <p class="tap-ask">This order was placed for you. Approve it, or reject it with a reason, before it can be billed.</p>

        <!-- Documents to review before approving -->
        <div v-if="docSections.length" class="track-appr-docs">
          <div v-for="(section, si) in docSections" :key="'s'+si" class="track-appr-docsection">
            <span class="track-appr-doclabel">{{ section.label }}<template v-if="section.name"> · {{ section.name }}</template></span>
            <div class="track-appr-chips">
              <button v-for="(doc, i) in section.docs" :key="i" class="track-doc-chip" @click="openDocModal(section.docs, i)">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span>{{ doc.title }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Approving is the expected outcome, so it carries the weight and
             rejecting is quieter. Two equally loud blocks make someone stop and
             read both before doing the ordinary thing. -->
        <template v-if="!approval.rejecting">
          <div class="tap-actions">
            <button class="tap-btn tap-btn--approve" :disabled="approval.saving" @click="approveThisOrder">Approve</button>
            <button class="tap-btn tap-btn--reject" :disabled="approval.saving" @click="approval.rejecting = true">Reject</button>
          </div>
        </template>
        <template v-else>
          <textarea v-model="approval.reason" rows="3" class="tap-textarea" placeholder="Why are you rejecting this order?"></textarea>
          <div class="tap-actions">
            <button class="tap-btn tap-btn--danger" :disabled="approval.saving" @click="rejectThisOrder">Submit rejection</button>
            <button class="tap-btn tap-btn--quiet" :disabled="approval.saving" @click="approval.rejecting = false">Cancel</button>
          </div>
        </template>
      </template>
    </div>

    <div class="track-outer-panel">
      <div class="track-panel" v-if="orderDetailsLoaded">

        <!-- Navy header -->
        <div class="track-panel-head">
          <div class="track-breadcrumb">
            <span class="track-breadcrumb-home" @click="router.push('/request')">Homepage</span>
            <span class="track-breadcrumb-sep">›</span>
            <span class="track-breadcrumb-current">Track Delivery</span>
          </div>

          <div class="track-order-title-row">
            <div class="track-order-number">{{ orderObject.data.order_details.order_number }}</div>
            <div
              class="track-status-badge"
              :style="{ background: statusColor.bg, color: statusColor.text }"
            >
              <span class="track-status-dot" :style="{ background: statusColor.dot }"></span>
              {{ humanizeStatus(orderObject.data.order_details.order_status_text) }}
            </div>
          </div>

          <div class="track-stops">
            <div v-for="(path, index) in headerStops" :key="index" class="track-stop-row">
              <template v-if="path._more">
                <div class="track-stop-icon-wrap"><div class="track-stop-line track-stop-line--full"></div></div>
                <div class="track-stop-label"><div class="track-stop-more">+{{ path._more }} more stop{{ path._more > 1 ? 's' : '' }}</div></div>
              </template>
              <template v-else>
                <div class="track-stop-icon-wrap">
                  <div class="track-stop-dot" :class="path._type === 'pickup' ? 'track-stop-dot--pickup' : 'track-stop-dot--drop'"></div>
                  <div class="track-stop-line" v-if="!path._last"></div>
                </div>
                <div class="track-stop-label">
                  <div class="track-stop-type">{{ path._type === 'pickup' ? 'Pick-up' : 'Drop-off' }}</div>
                  <div class="track-stop-name">{{ path.name }}</div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="track-panel-body">

          <!-- Pinned: driver card + tabs stay visible while a tab's content scrolls -->
          <div class="track-sticky">
          <!-- Uber-style driver card — shows whenever a driver is assigned -->
          <div class="track-driver-section" v-if="orderObject.data.driver_details">
            <div class="track-driver-head">
              <div class="track-driver-photo">
                <img v-if="orderObject.data.driver_details.photo" :src="orderObject.data.driver_details.photo" alt="" />
                <span v-else>{{ orderObject.data.driver_details.name?.charAt(0)?.toUpperCase() }}</span>
              </div>
              <div class="track-driver-id">
                <div class="track-driver-name">{{ orderObject.data.driver_details.name }}</div>
                <div class="track-driver-sub" v-if="orderObject.data.driver_details.partner">{{ orderObject.data.driver_details.partner }}</div>
                <div class="track-driver-stars" v-if="orderObject.data.driver_details.rating">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>
                  <span>{{ orderObject.data.driver_details.rating.avg }}</span>
                  <span class="track-driver-stars-count">({{ orderObject.data.driver_details.rating.count }})</span>
                </div>
              </div>
            </div>

            <div class="track-driver-vehicle-row">
              <div class="track-driver-vehicle-meta">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                <span>{{ orderObject.data.driver_details.vehicle_type || 'Vehicle' }}</span>
              </div>
              <span class="track-driver-plate-big" v-if="orderObject.data.driver_details.number_plate">{{ orderObject.data.driver_details.number_plate }}</span>
            </div>

            <a v-if="orderObject.data.driver_details.phone_number" :href="'tel:' + orderObject.data.driver_details.phone_number" class="track-driver-call">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              Call driver
            </a>
          </div>

          <div class="track-tabs">
            <button type="button" class="track-tab" :class="{ 'track-tab--on': trackTab === 'trip' }" @click="trackTab = 'trip'">Trip</button>
            <button type="button" class="track-tab" :class="{ 'track-tab--on': trackTab === 'activity' }" @click="trackTab = 'activity'">Activity</button>
            <button type="button" class="track-tab" :class="{ 'track-tab--on': trackTab === 'docs' }" @click="trackTab = 'docs'">Docs</button>
            <button type="button" class="track-tab" :class="{ 'track-tab--on': trackTab === 'feedback' }" @click="trackTab = 'feedback'">Feedback</button>
          </div>
          </div><!-- /track-sticky -->

          <!-- TAB: TRIP -->
          <div class="track-pane" v-if="trackTab === 'trip'">
          <div class="track-section">
            <div class="track-section-title">Order Details</div>
            <div class="track-info-grid">
              <div class="track-info-row">
                <span class="track-info-label">Amount</span>
                <span class="track-info-value">KES {{ orderObject.data.payment_details.customer_amount.toLocaleString() }}</span>
              </div>
              <div class="track-info-row">
                <span class="track-info-label">Placed on</span>
                <span class="track-info-value">{{ transformDate(orderObject.data.order_details.date_created) }}</span>
              </div>
              <div class="track-info-row" v-if="orderObject.data.order_details.order_status > 3">
                <span class="track-info-label">Delivered</span>
                <span class="track-info-value">
                  {{ transformDate(deliveredAt) }}
                </span>
              </div>
            </div>
          </div>

          <!-- ── Price Breakdown ─────────────────────────────────────────── -->
          <div class="track-section" v-if="orderObject.data.payment_details.pricing_model">
            <div class="track-section-title">Price Breakdown</div>

            <!-- Older orders with no captured snapshot -->
            <div v-if="!orderObject.data.payment_details.breakdown" class="track-empty-note">
              No price breakdown was captured for this order.
            </div>

            <div v-else class="track-info-grid">
              <!-- Distance model -->
              <template v-if="orderObject.data.payment_details.pricing_model === 'distance'">
                <div class="track-info-row">
                  <span class="track-info-label">Base fare
                    <span v-if="orderObject.data.payment_details.breakdown.base_distance"> ({{ orderObject.data.payment_details.breakdown.base_distance }} km incl.)</span>
                  </span>
                  <span class="track-info-value">{{ fmtMoney(orderObject.data.payment_details.breakdown.base_fee) }}</span>
                </div>
                <div class="track-info-row" v-if="orderObject.data.payment_details.breakdown.extra_km > 0">
                  <span class="track-info-label">{{ orderObject.data.payment_details.breakdown.extra_km }} km extra × {{ fmtMoney(orderObject.data.payment_details.breakdown.cost_per_km) }}/km</span>
                  <span class="track-info-value">{{ fmtMoney(orderObject.data.payment_details.breakdown.extra_km_cost) }}</span>
                </div>
                <div class="track-info-row" v-if="orderObject.data.payment_details.breakdown.extra_stops > 0">
                  <span class="track-info-label">{{ orderObject.data.payment_details.breakdown.extra_stops }} extra stop(s) × {{ fmtMoney(orderObject.data.payment_details.breakdown.cost_per_stop) }}</span>
                  <span class="track-info-value">{{ fmtMoney(orderObject.data.payment_details.breakdown.extra_stops_cost) }}</span>
                </div>
                <div class="track-info-row" v-if="orderObject.data.payment_details.distance_text">
                  <span class="track-info-label">Total distance</span>
                  <span class="track-info-value track-info-value--muted">{{ orderObject.data.payment_details.distance_text }}</span>
                </div>
              </template>

              <!-- Tonnage model -->
              <template v-else-if="orderObject.data.payment_details.pricing_model === 'tonnage'">
                <div class="track-info-row">
                  <span class="track-info-label">Goods weight</span>
                  <span class="track-info-value track-info-value--muted">{{ orderObject.data.payment_details.breakdown.tonnes }} t</span>
                </div>
                <div class="track-info-row">
                  <span class="track-info-label">{{ orderObject.data.payment_details.breakdown.rounded_km }} km × {{ orderObject.data.payment_details.breakdown.tonnes }} t × {{ fmtMoney(orderObject.data.payment_details.breakdown.tonnage_rate) }}/t/km</span>
                  <span class="track-info-value">{{ fmtMoney(orderObject.data.payment_details.breakdown.tonnage_cost) }}</span>
                </div>
                <div class="track-info-row" v-if="orderObject.data.payment_details.breakdown.extra_stops > 0">
                  <span class="track-info-label">{{ orderObject.data.payment_details.breakdown.extra_stops }} extra stop(s)</span>
                  <span class="track-info-value">{{ fmtMoney(orderObject.data.payment_details.breakdown.extra_stops_cost) }}</span>
                </div>
              </template>

              <!-- Location (fixed route) model -->
              <template v-else-if="orderObject.data.payment_details.pricing_model === 'location'">
                <div class="track-info-row" v-if="orderObject.data.payment_details.breakdown.route_name">
                  <span class="track-info-label">Route</span>
                  <span class="track-info-value track-info-value--muted">{{ orderObject.data.payment_details.breakdown.route_name }}</span>
                </div>
                <div class="track-info-row">
                  <span class="track-info-label">Fixed route price</span>
                  <span class="track-info-value">{{ fmtMoney(orderObject.data.payment_details.breakdown.fixed_price ?? orderObject.data.payment_details.customer_amount) }}</span>
                </div>
              </template>

              <!-- Flat model -->
              <template v-else-if="orderObject.data.payment_details.pricing_model === 'flat'">
                <div class="track-info-row">
                  <span class="track-info-label">Base fee</span>
                  <span class="track-info-value">{{ fmtMoney(orderObject.data.payment_details.breakdown.base_fee ?? orderObject.data.payment_details.customer_amount) }}</span>
                </div>
              </template>

              <div class="track-info-row track-info-row--total">
                <span class="track-info-label">Total</span>
                <span class="track-info-value">{{ fmtMoney(orderObject.data.payment_details.customer_amount) }}</span>
              </div>
            </div>
          </div>
          </div>

          <!-- TAB: ACTIVITY -->
          <div class="track-pane" v-else-if="trackTab === 'activity'">
          <div class="track-section track-section--activity" v-if="orderObject.data.delivery_logs?.length">
            <div class="track-section-title">Activity</div>
            <div class="track-timeline">
              <div
                v-for="(activity, index) in orderObject.data.delivery_logs"
                :key="index"
                class="track-timeline-item"
              >
                <div class="track-timeline-left">
                  <div class="track-timeline-dot" :class="index === orderObject.data.delivery_logs.length - 1 ? 'track-timeline-dot--active' : ''"></div>
                  <div class="track-timeline-connector" v-if="index < orderObject.data.delivery_logs.length - 1"></div>
                </div>
                <div class="track-timeline-content">
                  <div class="track-timeline-event">{{ extractTextUntilBy(activity.description) }}</div>
                  <div class="track-timeline-time">{{ transformDate(activity.log_time) }}</div>
                </div>
              </div>
            </div>
          </div>

          </div>

          <!-- TAB: DOCUMENTS -->
          <div class="track-pane" v-else-if="trackTab === 'docs'">
          <!-- ── Documents: Order documents, then grouped by delivery point ── -->
          <div class="track-section" v-for="(section, si) in docSections" :key="'ds'+si">
            <div class="track-section-title">{{ section.label }}<span v-if="section.name" class="track-docs-groupsub"> · {{ section.name }}</span></div>
            <div class="track-docs-grid">
              <div
                v-for="(doc, di) in section.docs"
                :key="di"
                class="track-doc-thumb"
                @click="openDocModal(section.docs, Number(di))"
              >
                <div class="track-doc-img-wrap">
                  <img
                    v-if="isImage(doc)"
                    :src="doc.src"
                    :alt="doc.title"
                    class="track-doc-img"
                    @error="(e) => { if (e.target) (e.target as HTMLElement).style.display='none' }"
                  />
                  <div v-else class="track-doc-file-icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#475569" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span class="track-doc-ext">{{ docExt(doc) }}</span>
                  </div>
                  <div class="track-doc-overlay">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  </div>
                </div>
                <div class="track-doc-label">{{ doc.title || ('Document ' + (Number(di) + 1)) }}</div>
              </div>
            </div>
          </div>

          <div class="track-section" v-if="!docSections.length">
            <div class="track-empty-tab">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <div class="track-empty-tab-text">Documents for this order will appear here.</div>
            </div>
          </div>
          </div>

          <!-- TAB: FEEDBACK -->
          <div class="track-pane" v-else-if="trackTab === 'feedback'">
          <!-- ── Rating ─────────────────────────────────────────────────── -->
          <div class="track-section" v-if="canGiveFeedback">
            <div class="track-section-title">Rate your delivery</div>

            <!-- Submitted (read-only) -->
            <template v-if="rating.submitted">
              <div class="track-stars track-stars--readonly">
                <svg v-for="s in 5" :key="s" class="track-star" :class="{ 'track-star--on': s <= rating.stars }" viewBox="0 0 24 24" width="24" height="24">
                  <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z"/>
                </svg>
              </div>
              <div class="track-rating-comment" v-if="rating.comment">“{{ rating.comment }}”</div>
              <div class="track-thanks">Thanks for rating</div>
            </template>

            <!-- Interactive -->
            <template v-else>
              <div class="track-stars" @mouseleave="rating.hover = 0">
                <svg
                  v-for="s in 5" :key="s"
                  class="track-star track-star--clickable"
                  :class="{ 'track-star--on': s <= (rating.hover || rating.stars) }"
                  viewBox="0 0 24 24" width="28" height="28"
                  @mouseenter="rating.hover = s"
                  @click="rating.stars = s"
                >
                  <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z"/>
                </svg>
              </div>
              <el-input
                v-model="rating.comment"
                type="textarea"
                :rows="2"
                maxlength="500"
                resize="none"
                placeholder="Add a comment (optional)"
                class="track-feedback-input"
              />
              <button class="track-submit-btn" :disabled="rating.saving || rating.stars < 1" @click="submitRating">
                {{ rating.saving ? 'Submitting…' : 'Submit rating' }}
              </button>
            </template>
          </div>

          <!-- ── Complaint (standalone, not gated behind a low rating) ───── -->
          <div class="track-section" v-if="canGiveFeedback">
            <div class="track-section-title">Something wrong?</div>

            <!-- Active complaint (Open / In Progress) -->
            <template v-if="complaint.existing && complaintIsActive">
              <div class="track-complaint-status track-complaint-status--active">
                <span class="track-complaint-badge track-complaint-badge--active">Complaint {{ complaint.existing.status }}</span>
                <span class="track-complaint-cat">{{ complaint.existing.category_label || complaint.existing.subject }}</span>
              </div>
              <div class="track-complaint-note">Our team is reviewing your complaint and will be in touch.</div>
            </template>

            <!-- Resolved / Dismissed complaint -->
            <template v-else-if="complaint.existing">
              <div class="track-complaint-status">
                <span
                  class="track-complaint-badge"
                  :class="complaint.existing.status === 'Resolved' ? 'track-complaint-badge--resolved' : 'track-complaint-badge--dismissed'"
                >Complaint {{ complaint.existing.status }}</span>
                <span class="track-complaint-cat">{{ complaint.existing.category_label || complaint.existing.subject }}</span>
              </div>
              <div class="track-complaint-note" v-if="complaint.existing.resolution_note">{{ complaint.existing.resolution_note }}</div>
            </template>

            <!-- No complaint yet -->
            <template v-else>
              <template v-if="!complaint.formOpen">
                <div class="track-complaint-prompt">If there was a problem with this delivery, let us know.</div>
                <button class="track-complaint-btn" @click="openComplaintForm">Raise a complaint</button>
              </template>

              <template v-else>
                <el-select v-model="complaint.category" placeholder="Choose a category" class="track-feedback-input" style="width:100%;">
                  <el-option
                    v-for="c in COMPLAINT_CATEGORIES"
                    :key="c.value"
                    :label="c.label"
                    :value="c.value"
                  />
                </el-select>
                <el-input
                  v-model="complaint.body"
                  type="textarea"
                  :rows="3"
                  maxlength="1000"
                  resize="none"
                  placeholder="Add any details (optional)"
                  class="track-feedback-input"
                />
                <div class="track-complaint-actions">
                  <button class="track-submit-btn track-submit-btn--ghost" :disabled="complaint.saving" @click="closeComplaintForm">Cancel</button>
                  <button class="track-submit-btn" :disabled="complaint.saving || !complaint.category" @click="submitComplaint">
                    {{ complaint.saving ? 'Submitting…' : 'Submit complaint' }}
                  </button>
                </div>
              </template>
            </template>
          </div>

          <div class="track-section" v-if="!canGiveFeedback">
            <div class="track-empty-tab">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z"/></svg>
              <div class="track-empty-tab-text">You can rate this delivery and report any issues once it's been delivered.</div>
            </div>
          </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Floating action buttons — bottom right -->
    <div class="track-fab-group">
      
      
<button 
  class="track-fab track-fab--green" 
  @click="router.push('/request')"
  style="display: flex; align-items: center; justify-content: flex-start; gap: 8px; text-align: left;"
>
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
  New Order
</button>

<button 
  class="track-fab track-fab--purple" 
  @click="router.push('/transactions/orders')"
  style="display: flex; align-items: center; justify-content: flex-start; gap: 8px; text-align: left;"
>
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1 2 1-2-1Z" />
    <line x1="16" y1="8" x2="8" y2="8" />
    <line x1="16" y1="12" x2="8" y2="12" />
    <line x1="10" y1="16" x2="8" y2="16" />
  </svg>
  Order History
</button>


    </div>

    <GMapMap
      :center="mapCenter"
      :zoom="12"
      map-type-id="roadmap"
      :options="mapOptions"
      style="position:absolute; top:0; left:0; right:0; bottom:0; width:100%; height:100%;"
      ref="myMapRef"
    >
      <template v-if="orderDetailsLoaded && mapPaths.length">
        <GMapMarker
          v-for="(m, index) in mapPaths"
          :key="index"
          :position="{ lat: m.lat, lng: m.lng }"
          :clickable="true"
          :draggable="false"
          :icon="getMarkerIcon(index)"
          @click="openStopInfo(index)"
        />
      </template>
      <!-- Driver live location -->
      <GMapMarker
        v-if="driverMarker"
        :position="driverMarker"
        :clickable="true"
        :draggable="false"
        :icon="driverMarkerIcon"
        @click="openDriverInfo"
      />
      <GMapInfoWindow
        v-if="activeInfo"
        :position="activeInfo.position"
        :opened="true"
        @closeclick="activeInfo = null"
      >
        <div style="font-family:'DM Sans',sans-serif;min-width:140px;max-width:230px;padding:2px 4px 4px;">
          <div style="font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#0061eb;margin-bottom:3px;">{{ activeInfo.eyebrow }}</div>
          <div style="font-size:14px;font-weight:700;color:#0f1729;line-height:1.3;">{{ activeInfo.title }}</div>
          <div v-for="(ln, i) in activeInfo.lines" :key="i" style="font-size:12px;color:#5b6472;margin-top:3px;">{{ ln }}</div>
        </div>
      </GMapInfoWindow>
      <GMapPolyline
        v-if="routePath.length > 1"
        :path="routePath"
        :options="polylineOptions"
      />
    </GMapMap>

    <!-- ── Delivery-document lightbox ──────────────────────────────────── -->
    <Teleport to="body">
      <Transition name="track-dm-fade">
        <div v-if="docModal.open" class="track-dm-backdrop" @click.self="closeDocModal">
          <div class="track-dm-modal">
            <!-- Header: title + nav + actions -->
            <div class="track-dm-header">
              <div class="track-dm-header-left">
                <div class="track-dm-header-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0061eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <div class="track-dm-header-text">
                  <div class="track-dm-title">{{ docModal.docs[docModal.active]?.title || 'Document' }}</div>
                  <div class="track-dm-subtitle">
                    {{ orderObject.data?.order_details?.order_number || '' }}
                    <span v-if="docModal.docs[docModal.active]?.approved" class="track-dm-approved">Reviewed and approved</span>
                  </div>
                </div>
              </div>
              <div class="track-dm-actions">
                <template v-if="docModal.docs.length > 1">
                  <button class="track-dm-navbtn" @click="docModal.active = (docModal.active - 1 + docModal.docs.length) % docModal.docs.length">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span class="track-dm-counter">{{ docModal.active + 1 }} / {{ docModal.docs.length }}</span>
                  <button class="track-dm-navbtn" @click="docModal.active = (docModal.active + 1) % docModal.docs.length">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  <div class="track-dm-divider"></div>
                </template>
                <a :href="docModal.docs[docModal.active]?.src" target="_blank" rel="noopener" class="track-dm-btn track-dm-btn--primary">Open ↗</a>
                <a :href="docModal.docs[docModal.active]?.src" :download="docModal.docs[docModal.active]?.title || 'document'" class="track-dm-btn track-dm-btn--ghost">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download
                </a>
                <button class="track-dm-close" @click="closeDocModal">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            <!-- Body: doc-list sidebar + big viewer -->
            <div class="track-dm-body">
              <div class="track-dm-sidebar" v-if="docModal.docs.length > 1">
                <button
                  v-for="(doc, i) in docModal.docs"
                  :key="i"
                  class="track-dm-side-item"
                  :class="{ 'track-dm-side-item--active': docModal.active === i }"
                  @click="docModal.active = Number(i)"
                >
                  <div class="track-dm-side-icon">
                    <img v-if="isImage(doc)" :src="doc.src" :alt="doc.title" class="track-dm-side-thumb" />
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div class="track-dm-side-text">
                    <span class="track-dm-side-title">{{ doc.title || 'Document ' + (Number(i) + 1) }}</span>
                    <span class="track-dm-side-sub">{{ docExt(doc) }}</span>
                  </div>
                </button>
              </div>
              <div class="track-dm-viewer">
                <img
                  v-if="docModal.docs[docModal.active] && isImage(docModal.docs[docModal.active])"
                  :src="docModal.docs[docModal.active].src"
                  :alt="docModal.docs[docModal.active].title"
                  class="track-dm-img"
                />
                <iframe
                  v-else-if="docModal.docs[docModal.active]"
                  :src="docModal.docs[docModal.active].src"
                  class="track-dm-iframe"
                  frameborder="0"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

/* Customer approval — floats top-right over the map.
   Neutral card. The status is carried by the pill and its dot, not by a band
   of colour across the top: a full-width amber or green wash competes with the
   map behind it and reads as an alarm rather than a state. */
.tap-card {
  position: absolute;
  top: 24px;
  right: 30px;
  z-index: 50;
  width: 340px;
  max-width: calc(100% - 60px);
  background: #fff;
  border: 1px solid #e3e9f2;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.10);
  padding: 15px 17px;
  font-family: 'DM Sans', sans-serif;
}

.tap-head {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding-bottom: 11px; border-bottom: 1px solid #eef2f7;
}
.tap-eyebrow {
  font-size: 0.66rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.07em; color: #8095bd;
}

/* The indicator. A dot plus the word, in a pill that borrows the status hue at
   low saturation -- the same shape the order list uses, so the two screens
   agree about what a status looks like. */
.tap-status {
  display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
  padding: 3px 9px 3px 8px; border-radius: 999px;
  font-size: 0.7rem; font-weight: 600; letter-spacing: 0.01em;
  border: 1px solid transparent; white-space: nowrap;
}
.tap-status-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; background: currentColor; }
.tap-status--pending  { color: #b45309; background: #fffbeb; border-color: #fcd9a4; }
.tap-status--approved { color: #15803d; background: #f2fdf5; border-color: #b7e4c7; }
.tap-status--rejected { color: #b91c1c; background: #fef4f4; border-color: #f2c2c2; }

/* Thread */
.tap-thread { margin: 12px 0 0; max-height: 230px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; }
.tap-row { display: flex; justify-content: flex-start; }
.tap-row--you { justify-content: flex-end; }
.tap-msg { max-width: 86%; background: #f8fafc; border: 1px solid #e9eef5; border-radius: 10px; padding: 8px 11px; }
.tap-msg--you { background: #f0f6ff; border-color: #dbe7fb; }
.tap-msg-head { display: flex; align-items: center; gap: 6px; margin-bottom: 2px; flex-wrap: wrap; }
.tap-msg-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.tap-msg-who { font-size: 0.72rem; font-weight: 700; color: #334155; }
.tap-msg-tag { font-size: 0.62rem; font-weight: 700; padding: 1px 6px; border-radius: 5px; }
.tap-msg-note { font-size: 0.8rem; color: #475569; margin: 0; line-height: 1.45; white-space: pre-line; }
.tap-msg-time { font-size: 0.62rem; color: #94a3b8; margin: 3px 0 0; }
.tap-legacy { font-size: 0.8rem; color: #7f1d1d; margin: 10px 0 0; line-height: 1.45; }
.tap-ask { font-size: 0.82rem; color: #475569; margin: 11px 0 12px; line-height: 1.5; }

/* Actions. Approve is the expected outcome and carries the weight; reject is
   an outline, so the pair does not read as two alarms of equal urgency. */
.tap-actions { display: flex; gap: 8px; }
.tap-btn {
  flex: 1; height: 38px; border-radius: 9px; font-family: inherit;
  font-size: 0.84rem; font-weight: 600; cursor: pointer;
  border: 1.5px solid transparent; transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.tap-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.tap-btn--approve { background: #15803d; border-color: #15803d; color: #fff; }
.tap-btn--approve:hover:not(:disabled) { background: #126c34; border-color: #126c34; }
.tap-btn--reject { background: #fff; border-color: #e6cfcf; color: #b91c1c; }
.tap-btn--reject:hover:not(:disabled) { background: #fef4f4; border-color: #d99a9a; }
.tap-btn--danger { background: #b91c1c; border-color: #b91c1c; color: #fff; }
.tap-btn--danger:hover:not(:disabled) { background: #a11717; border-color: #a11717; }
.tap-btn--quiet { flex: 0 0 auto; padding: 0 16px; background: #fff; border-color: #dbe7fb; color: #4a5f82; }
.tap-btn--quiet:hover:not(:disabled) { background: #f0f6ff; }
.tap-textarea {
  width: 100%; margin: 0 0 8px; padding: 9px 11px;
  border: 1.5px solid #dbe7fb; border-radius: 9px;
  font-family: inherit; font-size: 0.84rem; color: #1f3358;
  resize: vertical; box-sizing: border-box; outline: none;
}
.tap-textarea:focus { border-color: #0061eb; }
/* Document chips inside the approval card */
.track-appr-docs { margin: 0 0 12px; }
.track-appr-doclabel { display: block; font-size: 0.62rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; margin: 0 0 6px; }
.track-appr-docsection { margin-bottom: 12px; }
.track-appr-docsection:last-child { margin-bottom: 0; }
.track-docs-groupsub { font-weight: 500; color: #94a3b8; }
.track-appr-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 10px; }
.track-appr-chips:last-child { margin-bottom: 0; }
.track-doc-chip {
  display: inline-flex; align-items: center; gap: 5px; max-width: 100%;
  padding: 5px 10px; border: 1px solid #ccdeff; border-radius: 8px;
  background: #f0f6ff; color: #0061eb; font-family: inherit; font-size: 0.72rem;
  font-weight: 600; cursor: pointer; transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.track-doc-chip:hover { background: #0061eb; color: #fff; border-color: #0061eb; }
.track-doc-chip svg { flex-shrink: 0; }
.track-doc-chip span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
@media (max-width: 820px) {
  .tap-card { top: 12px; left: 12px; right: 12px; width: auto; max-width: none; }
}

.track-outer-panel {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  /*max-width: 1280px; */
  margin: 0 auto;
  padding: 0 30px;
  pointer-events: none;
  z-index: 10;
}

.track-panel {
  position: absolute;
  top: 24px;
  left: 30px;
  width: 400px;
  max-height: calc(100vh - 110px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0,97,235,0.16), 0 2px 8px rgba(0,97,235,0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  pointer-events: all;
  font-family: 'DM Sans', sans-serif;
}
.track-panel::-webkit-scrollbar { display: none; }

/* ── Navy header ──────────────────────────── */
.track-panel-head {
  background: linear-gradient(135deg, #0061eb 0%, #003a96 100%);
  padding: 13px 18px 12px;
  border-radius: 16px 16px 0 0;
  flex-shrink: 0;
}

.track-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.track-breadcrumb-home {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.5);
  cursor: pointer;
  transition: color 0.15s;
}
.track-breadcrumb-home:hover { color: rgba(255,255,255,0.85); }
.track-breadcrumb-sep { color: rgba(255,255,255,0.25); font-size: 0.75rem; }
.track-breadcrumb-current { font-size: 0.75rem; color: rgba(255,255,255,0.85); font-weight: 500; }

.track-order-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 11px;
  gap: 8px;
}
.track-order-number {
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.03em;
}
.track-status-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  /* A hairline of white so the pill has an edge against the navy rather than
     floating as a slightly paler smudge. */
  border: 1px solid rgba(255, 255, 255, 0.22);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.track-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.track-stops { display: flex; flex-direction: column; }
.track-stop-row { display: flex; gap: 10px; align-items: flex-start; }
.track-stop-icon-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 3px;
}
.track-stop-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.4);
  flex-shrink: 0;
}
.track-stop-dot--pickup { background: #22c55e; }
.track-stop-dot--drop { background: #f59e0b; }
.track-stop-line {
  width: 2px;
  height: 9px;
  background: rgba(255,255,255,0.15);
  margin: 2px 0;
}
.track-stop-line--full { height: 14px; }
.track-stop-label { flex: 1; padding-bottom: 5px; }
.track-stop-more {
  font-size: 0.73rem;
  color: rgba(255,255,255,0.55);
  font-weight: 500;
  padding: 1px 0;
}
.track-stop-type {
  font-size: 0.67rem;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  margin-bottom: 1px;
}
.track-stop-name {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.88);
  line-height: 1.3;
}

/* ── Body ─────────────────────────────────── */
.track-panel-body {
  padding: 14px 14px 22px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
}
.track-panel-body::-webkit-scrollbar { display: none; }

/* Driver card + tab bar stay pinned while a long tab (Activity) scrolls under them */
.track-sticky {
  position: sticky;
  top: 0;
  z-index: 5;
  flex-shrink: 0;
  background: #f8fafc;
  padding-bottom: 2px;
}
.track-tabs {
  display: flex;
  gap: 3px;
  background: #eef2f7;
  border-radius: 10px;
  padding: 3px;
  margin: 6px 0 4px;
}
.track-tab {
  flex: 1;
  border: none;
  background: transparent;
  padding: 7px 4px;
  border-radius: 7px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.76rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.track-tab:hover { color: #374151; }
.track-tab--on {
  background: #ffffff;
  color: #0061eb;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.track-empty-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 34px 16px;
  text-align: center;
}
.track-empty-tab-text {
  font-size: 0.8rem;
  color: #9ca3af;
  line-height: 1.45;
  max-width: 230px;
}

.track-section {
  background: white;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  padding: 13px 15px;
  margin-bottom: 10px;
}
.track-section-title {
  font-size: 0.67rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 10px;
}

.track-info-grid { display: flex; flex-direction: column; gap: 8px; }
.track-info-row { display: flex; justify-content: space-between; align-items: flex-start; }
.track-info-label { font-size: 0.8rem; color: #9ca3af; font-weight: 500; }
.track-info-value {
  font-size: 0.82rem;
  color: #0061eb;
  font-weight: 600;
  text-align: right;
  max-width: 60%;
}

/* ── Driver card (white + compact, one blue accent) ─────────────────── */
.track-driver-section {
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 8px;
}
.track-driver-head { display: flex; align-items: center; gap: 11px; }
.track-driver-photo {
  width: 44px; height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0061eb 0%, #003a96 100%);
  color: #fff;
  font-size: 1.1rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; flex-shrink: 0;
}
.track-driver-photo img { width: 100%; height: 100%; object-fit: cover; }
.track-driver-id { flex: 1; min-width: 0; }
.track-driver-section .track-driver-name { color: #111827; font-size: 0.92rem; font-weight: 700; }
.track-driver-sub { font-size: 0.75rem; color: #6b7280; margin-top: 1px; }
.track-driver-stars {
  display: inline-flex; align-items: center; gap: 4px;
  margin-top: 3px;
  color: #f59e0b; font-size: 0.78rem; font-weight: 700;
}
.track-driver-stars-count { color: #9ca3af; font-weight: 500; }
.track-driver-vehicle-row {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-top: 11px; padding-top: 11px;
  border-top: 1px solid #f0f1f3;
}
.track-driver-vehicle-meta {
  display: flex; align-items: center; gap: 7px;
  color: #374151; font-size: 0.8rem; font-weight: 600;
}
.track-driver-plate-big {
  background: #f3f4f6; color: #1f2937;
  font-weight: 800; letter-spacing: 0.07em;
  font-size: 0.82rem;
  border-radius: 6px; padding: 3px 11px;
  border: 1px solid #e5e7eb;
  font-family: 'DM Sans', sans-serif;
}
.track-driver-call {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  margin-top: 11px;
  background: #0061eb; color: #fff;
  font-size: 0.83rem; font-weight: 700;
  border-radius: 10px; padding: 10px;
  text-decoration: none;
  transition: background 0.15s, transform 0.1s;
}
.track-driver-call:hover { background: #0050c7; }
.track-driver-call:active { transform: scale(0.99); }

.track-driver-card { display: flex; align-items: center; gap: 12px; }
.track-driver-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0061eb 0%, #003a96 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  flex-shrink: 0;
}
.track-driver-name { font-size: 0.88rem; font-weight: 600; color: #0061eb; }
.track-driver-meta { font-size: 0.78rem; color: #6b7280; margin-top: 2px; }
.track-driver-plate {
  display: inline-block;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #374151;
  letter-spacing: 0.06em;
  margin-top: 5px;
}

.track-timeline { display: flex; flex-direction: column; }
.track-timeline-item { display: flex; gap: 10px; align-items: flex-start; }
.track-timeline-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 3px;
}
.track-timeline-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d1d5db;
  box-shadow: 0 0 0 2px #f3f4f6;
  flex-shrink: 0;
}
.track-timeline-dot--active {
  background: #0061eb;
  box-shadow: 0 0 0 2px rgba(0,97,235,0.2);
}
.track-timeline-connector {
  width: 2px;
  flex: 1;
  min-height: 16px;
  background: #e5e7eb;
  margin: 3px 0;
}
.track-timeline-content { flex: 1; padding-bottom: 14px; }
.track-timeline-event { font-size: 0.82rem; font-weight: 500; color: #374151; line-height: 1.3; }
.track-timeline-time { font-size: 0.72rem; color: #9ca3af; margin-top: 3px; }

/* ── Floating action buttons ──────────────── */
.track-fab-group {
  position: absolute;
  bottom: 100px;
  right: 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 10;
  pointer-events: all;
}
.track-fab {
  height: 44px;
  padding: 0 22px;
  border-radius: 22px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
  letter-spacing: 0.02em;
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15), 0 1.5px 4px rgba(0,0,0,0.08);
  width:200px;
}
.track-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.1);
}
.track-fab:active { transform: translateY(0); }
.track-fab--green {
  background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
  color: white;
}
.track-fab--purple {
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  color: white;
}

/* ── Price Breakdown extras ───────────────── */
.track-empty-note { font-size: 0.78rem; color: #9ca3af; line-height: 1.4; }
.track-info-value--muted { color: #6b7280; font-weight: 500; }
.track-info-row--total {
  border-top: 1.5px solid #e5e7eb;
  margin-top: 4px;
  padding-top: 9px;
}
.track-info-row--total .track-info-label { color: #374151; font-weight: 700; font-size: 0.84rem; }
.track-info-row--total .track-info-value { color: #0061eb; font-weight: 800; font-size: 0.9rem; }

/* ── Driver vehicle tag ───────────────────── */
.track-driver-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 5px; }
.track-driver-tags .track-driver-plate { margin-top: 0; }
.track-driver-vehicle {
  display: inline-block;
  background: #eff6ff;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #0061eb;
  letter-spacing: 0.02em;
}

/* ── Delivery documents grid ──────────────── */
.track-docs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
}
.track-doc-thumb { display: flex; flex-direction: column; gap: 5px; cursor: pointer; }
.track-doc-img-wrap {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 1;
  background: #eff6ff;
  border: 1.5px solid #e5e7eb;
  transition: border-color 0.15s;
}
.track-doc-thumb:hover .track-doc-img-wrap { border-color: #0061eb; }
.track-doc-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.track-doc-file-icon {
  width: 100%; height: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
}
.track-doc-ext { font-size: 0.6rem; font-weight: 700; color: #475569; letter-spacing: 0.04em; }
.track-doc-overlay {
  position: absolute; inset: 0;
  background: rgba(0,97,235,0.55);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity 0.18s;
}
.track-doc-thumb:hover .track-doc-overlay { opacity: 1; }
.track-doc-label {
  font-size: 0.68rem; color: #6b7280; font-weight: 500;
  text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* ── Rating ───────────────────────────────── */
.track-stars { display: flex; gap: 4px; align-items: center; }
.track-stars--readonly { margin-bottom: 4px; }
.track-star { fill: #e5e7eb; transition: fill 0.12s, transform 0.1s; }
.track-star--on { fill: #f59e0b; }
.track-star--clickable { cursor: pointer; }
.track-star--clickable:hover { transform: scale(1.12); }
.track-rating-comment { font-size: 0.8rem; color: #4b5563; font-style: italic; margin: 6px 0 2px; line-height: 1.4; }
.track-thanks { font-size: 0.72rem; font-weight: 600; color: #16a34a; margin-top: 4px; }
.track-feedback-input { margin-top: 10px; width: 100%; }
.track-submit-btn {
  margin-top: 10px;
  height: 38px;
  width: 100%;
  border: none;
  border-radius: 8px;
  background: #0061eb;
  color: white;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}
.track-submit-btn:hover:not(:disabled) { background: #0052c7; }
.track-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.track-submit-btn--ghost {
  background: white;
  color: #6b7280;
  border: 1.5px solid #e5e7eb;
}
.track-submit-btn--ghost:hover:not(:disabled) { background: #f9fafb; color: #374151; }

/* ── Complaint ────────────────────────────── */
.track-complaint-prompt { font-size: 0.8rem; color: #6b7280; line-height: 1.4; margin-bottom: 10px; }
.track-complaint-btn {
  height: 38px;
  width: 100%;
  border: 1.5px solid #fca5a5;
  border-radius: 8px;
  background: #fef2f2;
  color: #dc2626;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}
.track-complaint-btn:hover { background: #dc2626; color: white; border-color: #dc2626; }
.track-complaint-status { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.track-complaint-badge {
  display: inline-flex; align-items: center;
  padding: 3px 10px; border-radius: 20px;
  font-size: 0.7rem; font-weight: 700;
  border: 1.5px solid currentColor;
}
.track-complaint-badge--active    { color: #d97706; background: #fffbeb; border-color: #fcd34d; }
.track-complaint-badge--resolved  { color: #16a34a; background: #f0fdf4; border-color: #86efac; }
.track-complaint-badge--dismissed { color: #6b7280; background: #f3f4f6; border-color: #d1d5db; }
.track-complaint-cat { font-size: 0.78rem; color: #374151; font-weight: 600; }
.track-complaint-note { font-size: 0.76rem; color: #6b7280; margin-top: 8px; line-height: 1.45; }
.track-complaint-actions { display: flex; gap: 8px; }
.track-complaint-actions .track-submit-btn { width: auto; flex: 1; }

/* ── Document lightbox ────────────────────── */
.track-dm-backdrop {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(10, 20, 40, 0.65);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.track-dm-modal {
  background: white; border-radius: 16px;
  width: 94vw; max-width: 1300px; height: 92vh; max-height: 92vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.3);
  overflow: hidden;
  font-family: 'DM Sans', sans-serif;
}
.track-dm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1.5px solid #eef2f7; flex-shrink: 0;
}
.track-dm-header-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.track-dm-header-icon {
  width: 34px; height: 34px; border-radius: 9px; background: #eff6ff;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.track-dm-title { font-size: 0.9rem; font-weight: 700; color: #0f1729; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 340px; }
.track-dm-subtitle { font-size: 0.74rem; color: #9ca3af; font-weight: 500; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 460px; }
/* Says out loud what the stamp says on the image, for the documents that cannot
   carry one — and as confirmation on the ones that can. */
.track-dm-approved { color: #15803d; font-weight: 700; }
.track-dm-approved::before { content: '·'; color: #d1d5db; font-weight: 500; margin: 0 5px; }
.track-dm-close {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: #f0f6ff; color: #4a5f82; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s; flex-shrink: 0;
}
.track-dm-close:hover { background: #fee2e2; color: #dc2626; }
.track-dm-body { flex: 1; display: flex; overflow: hidden; min-height: 0; }
.track-dm-viewer { flex: 1; min-width: 0; background: #e5e7eb; overflow: auto; display: flex; align-items: center; justify-content: center; }
.track-dm-img { max-width: 100%; max-height: 100%; object-fit: contain; }
.track-dm-iframe { width: 100%; height: 100%; border: none; background: white; }
.track-dm-header-text { min-width: 0; }
.track-dm-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.track-dm-navbtn { width: 30px; height: 30px; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
.track-dm-navbtn:hover { background: #f8fafc; border-color: #cbd5e1; color: #334155; }
.track-dm-counter { font-size: 0.72rem; font-weight: 700; color: #64748b; min-width: 42px; text-align: center; }
.track-dm-divider { width: 1px; height: 20px; background: #e2e8f0; margin: 0 2px; }
.track-dm-btn { display: inline-flex; align-items: center; gap: 5px; font-size: 0.74rem; font-weight: 700; padding: 6px 12px; border-radius: 8px; text-decoration: none; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.track-dm-btn--primary { color: #0061eb; border: 1px solid #0061eb; background: #fff; }
.track-dm-btn--primary:hover { background: #0061eb; color: #fff; }
.track-dm-btn--ghost { color: #475569; border: 1px solid #cbd5e1; background: #fff; }
.track-dm-btn--ghost:hover { background: #f8fafc; border-color: #94a3b8; }
.track-dm-sidebar { width: 210px; flex-shrink: 0; border-right: 1.5px solid #eef2f7; background: #f8fafc; display: flex; flex-direction: column; gap: 4px; overflow-y: auto; padding: 8px; }
.track-dm-side-item { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left; padding: 8px 9px; border: 1px solid transparent; border-radius: 9px; background: transparent; cursor: pointer; transition: all 0.15s; }
.track-dm-side-item:hover { background: #eef4ff; }
.track-dm-side-item--active { background: #e8f0ff; border-color: #93c5fd; }
.track-dm-side-icon { width: 34px; height: 34px; border-radius: 7px; background: #eef2f7; display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden; }
.track-dm-side-thumb { width: 100%; height: 100%; object-fit: cover; }
.track-dm-side-text { min-width: 0; display: flex; flex-direction: column; }
.track-dm-side-title { font-size: 0.76rem; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.track-dm-side-sub { font-size: 0.64rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; }
.track-dm-nav {
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: rgba(255,255,255,0.18); color: white; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s; backdrop-filter: blur(4px);
}
.track-dm-nav:hover { background: rgba(255,255,255,0.32); }
.track-dm-nav--prev { left: 12px; }
.track-dm-nav--next { right: 12px; }
.track-dm-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px; border-top: 1.5px solid #eef2f7; flex-shrink: 0; gap: 12px;
}
.track-dm-count { font-size: 0.78rem; color: #9ca3af; font-weight: 600; }
.track-dm-download {
  display: inline-flex; align-items: center; gap: 6px;
  margin-left: auto;
  background: #0061eb; color: white; border-radius: 8px;
  padding: 8px 16px; font-size: 0.8rem; font-weight: 700;
  text-decoration: none; transition: background 0.15s;
}
.track-dm-download:hover { background: #0052c7; }
.track-dm-fade-enter-active { transition: opacity 0.2s; }
.track-dm-fade-leave-active { transition: opacity 0.16s; }
.track-dm-fade-enter-from, .track-dm-fade-leave-to { opacity: 0; }
</style>